<template>
  <AgGridVue
    class="ag-theme-eds atb-w-full atb-h-full"
    :class="gridClass"
    :style="{ ...style }"
    :column-defs="columnDefs"
    :suppress-pagination-panel="true"
    :suppress-cell-selection="true"
    v-bind="{ ...mergedGridOptions }"
  />
  <Pagination
    v-if="pagination"
    v-model="pageSelected"
    :page-count="totalPages"
    :total-rows="totalRows"
    :page-size="rowsPerPage"
    @pageClick="handlePageChange"
    @pageSizeChange="onPageSizeChanged"
    class="atb-bg-white"
  />
</template>

<script
  lang="ts"
  setup
>
  import { AgGridVue } from 'ag-grid-vue3/lib/AgGridVue';
  import 'ag-grid-community/dist/styles/ag-grid.css';
  import '@experian/ag-theme-eds/dist/ag-theme-eds.css';
  import {
    GridApi,
    ColDef,
    ColGroupDef,
    GridOptions,
    GridReadyEvent,
    ColumnApi,
    PaginationChangedEvent,
    GetRowNodeIdFunc,
    RowSelectedEvent,
    IDatasource,
    CellClickedEvent,
  } from 'ag-grid-community';
  import { reactive, ref, toRefs, unref } from '@vue/reactivity';
  import { computed, Ref, watch, watchEffect } from 'vue';
  import Pagination from '@atb-ui/eds-vue/src/components/Pagination.vue';

  // TODO: We need to move to type folder as mentiones in ticket ATBX-13391
  export interface DataGridColumnDef extends ColDef {
    data?: string;
    title?: string;
  }
  // TODO: We need to move to type folder as mentiones in ticket ATBX-13391
  export type DataGridColGroupDef = ColGroupDef;
  // TODO: We need to move to type folder as mentiones in ticket ATBX-13391
  export interface HeaderProps {
    label: string;
    key: string;
  }
  // TODO: We need to move to type folder as mentiones in ticket ATBX-13391
  export interface DataGridProps
    extends Omit<GridOptions, 'suppressPaginationPanel'> {
    gridClass?: string;
    style?: Object;
    columnDefs?: (DataGridColumnDef | DataGridColGroupDef)[];
    sizeColumnsToFit?: boolean;
    rowsPerPageOptions?: string[];
    onDataGridReady?: (_gridReadyEvent: GridReadyEvent) => void;
    isServerside?: boolean;
    pagination?: boolean;
    rowData?: any[] | null;
    rowSelection?: string;
    rowMultiSelectWithClick: boolean;
    groupSelectsFiltered?: boolean;
    getRowNodeId?: GetRowNodeIdFunc;
    suppressRowClickSelection?: boolean;
    onRowSelected?(_event: RowSelectedEvent): void;
    datasource?: IDatasource;
    components?: any;
    context?: any;
    onCellClicked?(_event: CellClickedEvent): void;
    suppressExcelExport?: boolean;
    defaultColDef?: ColDef;
  }

  const props = withDefaults(defineProps<DataGridProps>(), {
    isServerside: false,
    pagination: true,
    suppressRowClickSelection: true,
    rowMultiSelectWithClick: false,
  });

  const {
    gridClass,
    style,
    quickFilterText = ref(''),
    rowData = ref([]),
    sizeColumnsToFit,
    rowSelection,
    rowMultiSelectWithClick,
    datasource,
    isServerside,
    onDataGridReady,
    pagination,
    columnDefs,
    getRowNodeId,
    suppressRowClickSelection,
    defaultColDef,
    ...rest
  } = toRefs(props);

  const paginationPageSize = ref(rest.paginationPageSize?.value || 10);
  const rows = computed(() => rowData.value || []);
  const gridApi: Ref<GridApi | null> = ref(null);
  const columnApi: Ref<ColumnApi | null> = ref(null);
  const currentPage = ref(0);
  const rowsPerPage = ref(10);
  const pageSize = computed(() => paginationPageSize.value);
  const totalRows = ref(rows.value?.length || 0);
  const totalPages = ref(Math.ceil((rows.value?.length || 0) / pageSize.value));
  const pageSelected = computed(() => currentPage.value + 1);

  const defaultGridOptions: Partial<GridOptions> = reactive({
    debounceVerticalScrollbar: true,
    defaultColDef: {
      sortable: true,
      resizable: true,
      flex: 1,
      minWidth: 150,
    },
    rowSelection: 'multiple',
    groupSelectsFiltered: true,
    rowGroupPanelShow: 'always',
    pivotPanelShow: 'always',
    animateRows: true,
    sideBar: {
      toolPanels: ['columns', 'filters'],
      defaultToolPanel: '',
    },
    rowData: rows,
    enableCellTextSelection: true,
  });

  const serverSideGridOptions: Partial<GridOptions> = reactive({
    defaultColDef: {
      sortable: true,
      resizable: true,
      flex: 1,
      minWidth: 150,
    },
    rowModelType: 'infinite',
    maxBlocksInCache: 0,
    cacheBlockSize: paginationPageSize.value,
    rowBuffer: 0,
    rowSelection: 'multiple',
    groupSelectsFiltered: true,
    cacheOverflowSize: 0,
    maxConcurrentDatasourceRequests: 1,
    infiniteInitialRowCount: paginationPageSize.value,
    enableCellTextSelection: true,
  });

  const gridOptions =
    datasource?.value !== undefined || isServerside.value
      ? serverSideGridOptions
      : defaultGridOptions;

  const onGridReady = (params: GridReadyEvent) => {
    gridApi.value = params.api;
    columnApi.value = params.columnApi;
    onDataGridReady?.value && onDataGridReady.value(params);
    sizeColumnsToFit?.value && params.api.sizeColumnsToFit();
  };

  const handlePageChange = (page: number) => {
    gridApi.value?.paginationGoToPage(page - 1);
  };

  const onPageSizeChanged = (newPageSize: number | string) => {
    rowsPerPage.value =
      newPageSize === 'ALL' ? totalRows.value : Number(newPageSize);
    paginationPageSize.value = rowsPerPage.value;
    gridApi.value?.paginationSetPageSize(rowsPerPage.value);
    currentPage.value = 0;
  };

  watch(quickFilterText, () => {
    gridApi.value?.setQuickFilter(quickFilterText?.value);
  });

  watch(rowData, () => {
    gridApi.value?.setRowData(rows.value);
  });

  watchEffect(() => {
    datasource?.value && gridApi.value?.setDatasource(datasource.value);
  });

  watch(
    gridApi,
    () => {
      datasource?.value && gridApi.value?.setDatasource(datasource.value);
    },
    { deep: true }
  );

  const onPaginationChanged = (_e: PaginationChangedEvent) => {
    if (gridApi.value) {
      totalPages.value = gridApi.value?.paginationGetTotalPages();
      currentPage.value = gridApi.value?.paginationGetCurrentPage();
      totalRows.value = gridApi.value?.paginationGetRowCount();
    }
  };

  const unwrappedRest = computed(() =>
    Object.fromEntries(
      Object.entries(rest).map(([key, val]) => [key, unref(val)])
    )
  );

  const mergedGridOptions = {
    ...gridOptions,
    defaultColDef: defaultColDef?.value,
    getRowNodeId: getRowNodeId?.value,
    rowSelection: rowSelection?.value,
    rowMultiSelectWithClick: rowMultiSelectWithClick?.value,
    suppressRowClickSelection: suppressRowClickSelection.value,
    ...unwrappedRest.value,
    paginationPageSize: paginationPageSize.value,
    pagination: pagination.value,
    onPaginationChanged(event: PaginationChangedEvent) {
      onPaginationChanged(event);
      rest.onPaginationChanged?.value && rest.onPaginationChanged.value(event);
    },
    onGridReady(event: GridReadyEvent) {
      onGridReady(event);
      rest.onGridReady?.value && rest.onGridReady.value(event);
    },
    onRowSelected(event: RowSelectedEvent) {
      rest.onRowSelected?.value && rest.onRowSelected.value(event);
    },
    onCellClicked(event: CellClickedEvent) {
      rest.onCellClicked?.value && rest.onCellClicked.value(event);
    },
  };
</script>
